import { format } from "date-fns"
import { Button } from "@/components/ui/button"
import type { ViewType } from "@/lib/calendar-types"
import { CalendarIcon } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons"

interface CalendarHeaderProps {
  view: ViewType
  currentDate: Date
  onViewChange: (view: ViewType) => void
  onPrevious: () => void
  onNext: () => void
  onToday: () => void
}

export default function CalendarHeader({
  view,
  currentDate,
  onViewChange,
  onPrevious,
  onNext,
  onToday,
}: CalendarHeaderProps) {
  const getHeaderText = () => {
    if (view === "day") {
      return format(currentDate, "MMMM d, yyyy")
    } else if (view === "week") {
      const startOfWeek = new Date(currentDate)
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay())
      const endOfWeek = new Date(startOfWeek)
      endOfWeek.setDate(startOfWeek.getDate() + 6)

      if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
        return `${format(startOfWeek, "MMMM d")} - ${format(endOfWeek, "d, yyyy")}`
      } else if (startOfWeek.getFullYear() === endOfWeek.getFullYear()) {
        return `${format(startOfWeek, "MMMM d")} - ${format(endOfWeek, "MMMM d, yyyy")}`
      } else {
        return `${format(startOfWeek, "MMMM d, yyyy")} - ${format(endOfWeek, "MMMM d, yyyy")}`
      }
    } else {
      return format(currentDate, "MMMM yyyy")
    }
  }

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-b">
      <div className="flex items-center mb-4 sm:mb-0">
        <Button variant="outline" size="icon" onClick={onPrevious} aria-label="Previous">
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" onClick={onNext} aria-label="Next" className="ml-1">
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
        <Button variant="outline" onClick={onToday} className="ml-2">
          Today
        </Button>
        <h2 className="text-xl font-semibold ml-4">{getHeaderText()}</h2>
      </div>

      <div className="flex items-center">
        <CalendarIcon className="mr-2 h-4 w-4" />
        <Select value={view} onValueChange={(value) => onViewChange(value as ViewType)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select view" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Day</SelectItem>
            <SelectItem value="week">Week</SelectItem>
            <SelectItem value="month">Month</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
